import { useEffect, useState } from 'react';
import {
  useCustomMutation,
  useInvalidate,
  useNotification,
  useTranslate,
} from '@refinedev/core';
import type { HttpError } from '@refinedev/core';
import { useSelect } from '@refinedev/antd';
import { Alert, Form, Input, Modal, Select } from 'antd';
import type {
  ContentImprintCreateIn,
  ContentImprintOut,
  ContentPublisherOut,
} from '@/api/generated/schemas';
import { Provider } from '@/api/generated/schemas';
import { extractBackendMessage } from '@/utils/apiError';

interface ImprintCreateValues {
  publisher_id: number;
  name: string;
}

interface ImprintCreateModalProps {
  open: boolean;
  onClose: () => void;
}

export const ImprintCreateModal = ({
  open,
  onClose,
}: ImprintCreateModalProps) => {
  const t = useTranslate();
  const { open: notify } = useNotification();
  const invalidate = useInvalidate();
  const { mutateAsync } = useCustomMutation<ContentImprintOut>();
  const [form] = Form.useForm<ImprintCreateValues>();
  const [submitting, setSubmitting] = useState(false);

  const tk = (suffix: string) => t(`content_catalog.imprints.create.${suffix}`);

  // Elibri imprints come only from the feed; the backend rejects the rest.
  const { selectProps } = useSelect<ContentPublisherOut>({
    resource: 'content_catalog.publishers',
    optionLabel: 'name',
    optionValue: 'id',
    filters: [{ field: 'provider', operator: 'eq', value: Provider.novelist }],
    onSearch: (value) => [{ field: 'q', operator: 'contains', value }],
    pagination: { pageSize: 20, mode: 'server' },
    queryOptions: { enabled: open },
  });

  useEffect(() => {
    if (open) {
      form.resetFields();
    } else {
      setSubmitting(false);
    }
  }, [open, form]);

  const handleSubmit = async () => {
    let values: ImprintCreateValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    setSubmitting(true);
    try {
      const payload: ContentImprintCreateIn = { name: values.name.trim() };
      await mutateAsync({
        url: `content_catalog/publishers/${values.publisher_id}/imprints`,
        method: 'post',
        values: payload,
        successNotification: false,
        errorNotification: false,
      });
      invalidate({
        resource: 'content_catalog.imprints',
        invalidates: ['list'],
      });
      notify?.({
        type: 'success',
        message: t('notifications.success'),
        description: tk('success'),
      });
      onClose();
    } catch (error) {
      const message = extractBackendMessage(error) ?? tk('error_fallback');
      // A name the publisher already has belongs next to the field.
      if ((error as HttpError).statusCode === 409) {
        form.setFields([{ name: 'name', errors: [message] }]);
      } else {
        notify?.({
          type: 'error',
          message: t('notifications.error'),
          description: message,
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      title={tk('title')}
      okText={tk('submit')}
      okButtonProps={{ loading: submitting }}
      cancelButtonProps={{ disabled: submitting }}
      closable={!submitting}
      maskClosable={!submitting}
      onCancel={onClose}
      onOk={handleSubmit}
      destroyOnClose
    >
      <Alert
        type="info"
        showIcon
        message={tk('hints.novelist_only')}
        style={{ marginBottom: 16 }}
      />
      <Form
        form={form}
        layout="vertical"
        preserve={false}
        disabled={submitting}
      >
        <Form.Item
          name="publisher_id"
          label={t('content_catalog.imprints.fields.publisher')}
          rules={[
            {
              required: true,
              message: t('content_catalog.imprints.errors.publisher_required'),
            },
          ]}
        >
          <Select
            {...selectProps}
            showSearch
            filterOption={false}
            placeholder={tk('publisher_placeholder')}
          />
        </Form.Item>
        <Form.Item
          name="name"
          label={t('content_catalog.imprints.fields.name')}
          extra={tk('hints.name')}
          rules={[
            {
              required: true,
              whitespace: true,
              message: t('content_catalog.imprints.errors.name_required'),
            },
            {
              max: 512,
              message: t('content_catalog.imprints.errors.max_length', {
                max: 512,
              }),
            },
          ]}
        >
          <Input />
        </Form.Item>
      </Form>
    </Modal>
  );
};
